import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ExclamationTriangleIcon,
    ShieldExclamationIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";

import { getFailedActions, getSensitiveActions } from "../api/auditApi";
import type { FailedActionsQueryParams, SensitiveActionsQueryParams } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtDate = (d: string) =>
    new Date(d).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });

const statusColors: Record<string, string> = {
    SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    FAILURE: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    PARTIAL: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    DENIED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const dayOptions = [7, 14, 30, 60, 90];

// ============================================================================
// Component
// ============================================================================

export const SecurityAuditRoute = () => {
    // ── Failed Actions State ──
    const [failedParams, setFailedParams] = useState<FailedActionsQueryParams>({
        days: 30,
        page: 0,
        size: 15,
    });

    // ── Sensitive Actions State ──
    const [sensitiveParams, setSensitiveParams] = useState<SensitiveActionsQueryParams>({
        days: 30,
        page: 0,
        size: 15,
    });

    // ── Queries ──
    const failedQ = useQuery({
        queryKey: ["audit-failed-actions", failedParams],
        queryFn: () => getFailedActions(failedParams),
    });

    const sensitiveQ = useQuery({
        queryKey: ["audit-sensitive-actions", sensitiveParams],
        queryFn: () => getSensitiveActions(sensitiveParams),
    });

    const failedData = failedQ.data?.data;
    const sensitiveData = sensitiveQ.data?.data;

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-8">
            {/* ======================================================== */}
            {/* FAILED ACTIONS                                            */}
            {/* ======================================================== */}
            <section className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <ShieldExclamationIcon className="h-5 w-5 text-rose-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            Failed Actions
                        </h3>
                        {failedData && (
                            <span className="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                                {failedData.totalElements}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400">
                            Past
                        </label>
                        <select
                            value={failedParams.days}
                            onChange={(e) =>
                                setFailedParams((p) => ({
                                    ...p,
                                    days: Number(e.target.value),
                                    page: 0,
                                }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            {dayOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n} days
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {failedQ.isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                        </div>
                    ) : failedQ.error ? (
                        <div className="px-5 py-8 text-center text-sm text-rose-500">
                            <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6" />
                            Failed to load failed actions
                        </div>
                    ) : !failedData?.logs?.length ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">
                            No failed actions found
                        </p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                                <tr>
                                    <th className="px-4 py-3 text-left">Action</th>
                                    <th className="px-4 py-3 text-left">Admin</th>
                                    <th className="px-4 py-3 text-left">Category</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Error</th>
                                    <th className="px-4 py-3 text-left">IP</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {failedData.logs.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white">
                                            {item.action}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {item.adminEmail}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {item.actionCategory}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[item.status] ?? statusColors.FAILURE
                                                    }`}
                                            >
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="max-w-[200px] truncate px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {item.errorMessage || "—"}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                                            {item.ipAddress}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {fmtDate(item.performedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {failedData && failedData.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            Page {failedData.currentPage + 1} of {failedData.totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={failedData.currentPage === 0}
                                onClick={() =>
                                    setFailedParams((p) => ({ ...p, page: p.page! - 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Previous
                            </button>
                            <button
                                disabled={failedData.currentPage + 1 >= failedData.totalPages}
                                onClick={() =>
                                    setFailedParams((p) => ({ ...p, page: p.page! + 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* ======================================================== */}
            {/* SENSITIVE ACTIONS                                         */}
            {/* ======================================================== */}
            <section className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <LockClosedIcon className="h-5 w-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            Sensitive Actions
                        </h3>
                        {sensitiveData && (
                            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {sensitiveData.totalElements}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400">
                            Past
                        </label>
                        <select
                            value={sensitiveParams.days}
                            onChange={(e) =>
                                setSensitiveParams((p) => ({
                                    ...p,
                                    days: Number(e.target.value),
                                    page: 0,
                                }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        >
                            {dayOptions.map((n) => (
                                <option key={n} value={n}>
                                    {n} days
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {sensitiveQ.isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                        </div>
                    ) : sensitiveQ.error ? (
                        <div className="px-5 py-8 text-center text-sm text-rose-500">
                            <ExclamationTriangleIcon className="mx-auto mb-2 h-6 w-6" />
                            Failed to load sensitive actions
                        </div>
                    ) : !sensitiveData?.logs?.length ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-400">
                            No sensitive actions found
                        </p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                                <tr>
                                    <th className="px-4 py-3 text-left">Action</th>
                                    <th className="px-4 py-3 text-left">Admin</th>
                                    <th className="px-4 py-3 text-left">Category</th>
                                    <th className="px-4 py-3 text-left">Resource</th>
                                    <th className="px-4 py-3 text-left">Summary</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {sensitiveData.logs.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white">
                                            {item.action}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {item.adminEmail}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            {item.actionCategory}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                            <span className="font-medium">{item.resourceType}</span>
                                        </td>
                                        <td className="max-w-[200px] truncate px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {item.changeSummary || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[item.status] ?? statusColors.SUCCESS
                                                    }`}
                                            >
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                                            {fmtDate(item.performedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {sensitiveData && sensitiveData.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            Page {sensitiveData.currentPage + 1} of {sensitiveData.totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={sensitiveData.currentPage === 0}
                                onClick={() =>
                                    setSensitiveParams((p) => ({ ...p, page: p.page! - 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Previous
                            </button>
                            <button
                                disabled={sensitiveData.currentPage + 1 >= sensitiveData.totalPages}
                                onClick={() =>
                                    setSensitiveParams((p) => ({ ...p, page: p.page! + 1 }))
                                }
                                className="rounded-lg border border-slate-300 px-3 py-1 text-xs disabled:opacity-40 dark:border-slate-700 dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};
